import React, { useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { SlidersHorizontal, Cookie } from "lucide-react";
import PageShell, { PageHero } from "./PageShell";
import { legalDocs } from "../content/legal";
import { getCookiePrefs } from "../lib/cookies";

import { tr } from "../i18n";
import { useLang } from "../i18n/LanguageProvider";
import { Languages } from "lucide-react";
const LEGAL_NOTICE = "This is a translation provided for convenience only. The English version of this document is legally binding. In the event of any discrepancy or difference in interpretation, the English version shall prevail.";

export default function LegalDoc() {
  const location = useLocation();
  const key = location.pathname.replace(/^\//, "");
  const doc = legalDocs[key];
  const { lang, setLang } = useLang();
  if (!doc) return <Navigate to="/" replace />;

  return (
    <PageShell>
      <PageHero eyebrow={tr("Legal")} title={doc.title} intro={doc.intro} />
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          {lang !== "en" && (
            <div data-testid="legal-binding-notice" className="flex gap-3 rounded-xl bg-[#f6f2e9] border-l-4 border-[#c2a15a] p-5 mb-10 text-[14px] text-[#0b1f3a]/80 leading-relaxed">
              <Languages size={18} className="shrink-0 mt-0.5 text-[#c2a15a]" />
              <div>
                <p>{tr(LEGAL_NOTICE)}</p>
                <button onClick={() => setLang("en")} data-testid="legal-switch-english"
                  className="mt-2 font-semibold text-[#0b1f3a] underline underline-offset-2 hover:text-[#c2a15a] transition-colors">
                  {tr("View the English version")}
                </button>
              </div>
            </div>
          )}
          <p className="text-[12px] uppercase tracking-widest text-[#c2a15a] mb-8">
            {doc.updated}
          </p>

          <div className="space-y-9">
            {doc.sections.map((s, i) => (
              <div key={i} className="relative pl-6">
                <span className="absolute left-0 top-1.5 w-2.5 h-2.5 rounded-full bg-[#c2a15a]" />
                <h2 className="font-display text-[24px] md:text-[26px] text-[#0b1f3a]">
                  {s.h}
                </h2>
                <p className="text-[#0b1f3a]/65 text-[16px] leading-relaxed mt-2.5">
                  {s.p}
                </p>
              </div>
            ))}
          </div>

          {doc.isCookies && <CookieManager />}
        </div>
      </section>
    </PageShell>
  );
}

function CookieManager() {
  const [, force] = useState(0);
  const prefs = getCookiePrefs();

  const openSettings = () => {
    window.dispatchEvent(new Event("rh:open-cookie-settings"));
    // reflect any saved change after the dialog likely closes
    setTimeout(() => force((x) => x + 1), 400);
  };

  const status = prefs
    ? [
        ["Necessary", "On", true],
        ["Analytics", prefs.analytics ? "On" : "Off", prefs.analytics],
        ["Marketing", prefs.marketing ? "On" : "Off", prefs.marketing],
      ]
    : null;

  return (
    <div className="mt-12 rounded-2xl bg-[#0b1f3a] p-7 md:p-8">
      <div className="flex items-center gap-3 mb-4">
        <Cookie size={20} className="text-[#c2a15a]" />
        <h3 className="font-display text-white text-[22px]">
          {tr("Your cookie preferences")}
        </h3>
      </div>
      {status ? (
        <div className="flex flex-wrap gap-2 mb-6">
          {status.map(([label, state, on]) => (
            <span
              key={label}
              className={`text-[12px] px-3 py-1.5 rounded-full border ${
                on
                  ? "border-[#c2a15a]/40 text-[#c2a15a]"
                  : "border-white/15 text-white/50"
              }`}
            >
              {tr(label)}: {tr(state)}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-white/60 text-[14px] mb-6">
          {tr("You haven't set a preference yet.")}
        </p>
      )}
      <button
        onClick={openSettings}
        className="inline-flex items-center gap-2 bg-[#c2a15a] hover:bg-[#d8bd82] text-[#0b1f3a] font-semibold text-[14px] px-6 py-3.5 rounded-full transition-colors"
      >
        <SlidersHorizontal size={16} /> {tr("Manage cookie preferences")}
      </button>
    </div>
  );
}
